import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
  Divider,
  Paper,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import WomanIcon from "@mui/icons-material/Woman";
import ManIcon from "@mui/icons-material/Man";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "../../../../components/index";
import { isEmptyObject, stringAvatar, stringToColor } from "../../../../utils";
import { SuittService } from "../../../../services";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
} from "@mui/material";
import { snackActions } from "../../../../utils/SnackBarUtils";

function MainUserInfo({ user = {}, getUserInfo = () => {} }) {
  const suittService = new SuittService();
  const [editUser, setEditUser] = useState({
    ...user,
    orgUnitCode: user?.orgUnit?.code || "",
  });
  const [orgUnits, setOrgUnits] = useState([]);
  const username = `${user.fullNameUA.firstName} ${user.fullNameUA.lastName} ${
    user.fullNameUA.middleName || ""
  }`.trim();
  const engUsername = !isEmptyObject(user.fullNameENG)
    ? `${user.fullNameENG.firstName} ${user.fullNameENG.lastName} ${
        user.fullNameENG.middleName || ""
      }`.trim()
    : "";

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const splittedName = name.split(".");
    if (splittedName.length > 1) {
      setEditUser({
        ...editUser,
        [splittedName[0]]: {
          ...editUser[splittedName[0]],
          [splittedName[1]]: value,
        },
      });
    } else {
      setEditUser({ ...editUser, [name]: value });
    }
  };

  const updateUserInfo = async () => {
    const result = await suittService.updateUserInfo({ user: editUser });
    if (!result || !result?.status)
      return snackActions.error("Щось пішло не так");

    snackActions.success("Дані успішно оновлено");
    getUserInfo();
  };

  useEffect(() => {
    (async () => {
      const res = await suittService.getOrgUnits();
      if (res?.status && Array.isArray(res.data)) {
        const list = res.data;
        const byParent = new Map();
        for (const u of list) {
          const key = u.parentCode ?? "__ROOT__";
          const arr = byParent.get(key) || [];
          arr.push(u);
          byParent.set(key, arr);
        }
        const ordered = [];
        const walk = (parentCode, depth) => {
          const key = parentCode ?? "__ROOT__";
          const arr = (byParent.get(key) || []).sort((a, b) =>
            a.nameUk.localeCompare(b.nameUk)
          );
          for (const u of arr) {
            ordered.push({
              ...u,
              _depth: depth,
              _indentLabel: `${"— ".repeat(depth)}${u.nameUk}`,
            });
            walk(u.code, depth + 1);
          }
        };
        walk(null, 0);
        setOrgUnits(ordered);
      }
    })();
  }, []);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Grid container sx={{ padding: "10px 0" }}>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Button onClick={updateUserInfo} variant={"contained"}>
            Оновити
          </Button>
        </Grid>
        <Grid
          item
          md={3}
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <Avatar
            alt={username}
            {...stringAvatar(username)}
            sx={{
              width: 200,
              height: 200,
              borderRadius: "8px",
              bgcolor: stringToColor(username),
            }}
          />
        </Grid>
        <Grid item md={9} sx={{ textAlign: "left" }}>
          <Stack spacing={2}>
            <Paper sx={{ p: 2 }}>
              <Typography component={"h2"} variant={"h5"}>{`${username} ${
                engUsername ? "(" + engUsername + ")" : ""
              }`}</Typography>
              <Box sx={{ display: "flex", alignItems: "center" }} marginTop={2}>
                <Typography
                  component={"p"}
                  variant={"p"}
                >{`Тип профілю: `}</Typography>
                <Stack direction="row" spacing={1} marginLeft={1}>
                  <Chip label={user.accountType} variant="outlined" />
                </Stack>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }} marginTop={2}>
                <Typography
                  component={"p"}
                  variant={"p"}
                >{`Стать:`}</Typography>
                {user.gender === "чол" ? (
                  <ManIcon sx={{ fontSize: "1.5em" }} />
                ) : (
                  <WomanIcon sx={{ fontSize: "1.5em" }} />
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }} marginTop={2}>
                <Typography
                  component={"p"}
                  variant={"p"}
                >{`UserId: ${user._id}`}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }} marginTop={2}>
                <Typography component={"p"} variant={"p"}>
                  {"Email:"}
                </Typography>
                <Typography component={"p"} variant={"p"} marginLeft={1}>
                  {
                    <a
                      href={`mailto:${user.email}`}
                      target={"_blank"}
                      rel="noreferrer"
                    >
                      {user.email}
                    </a>
                  }
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={orgUnits}
                      getOptionLabel={(o) => o._indentLabel || o.nameUk || ""}
                      isOptionEqualToValue={(o, v) => o.code === v.code}
                      value={
                        orgUnits.find((o) => o.code === editUser.orgUnitCode) ||
                        null
                      }
                      onChange={(e, val) =>
                        setEditUser({
                          ...editUser,
                          orgUnitCode: val?.code || "",
                        })
                      }
                      renderInput={(params) => (
                        <TextField {...params} label={"Підрозділ"} />
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <MobileDatePicker
                    label={"Дата народження"}
                    inputFormat="MM/dd/yyyy"
                    value={new Date(editUser.birthday)}
                    onChange={(newDate) =>
                      setEditUser({
                        ...editUser,
                        birthday: newDate,
                      })
                    }
                    renderInput={(params) => (
                      <TextField fullWidth {...params} />
                    )}
                    sx={{ width: "100%" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={"Ваш номер мобільного телефону #1"}
                    label={"Ваш номер мобільного телефону #1"}
                    variant="outlined"
                    fullWidth
                    value={editUser.contacts.tel1}
                    type={"number"}
                    name={"contacts.tel1"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть ваш номер мобільного телефону #1"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={"Ваш номер мобільного телефону #2"}
                    label={"Ваш номер мобільного телефону #2"}
                    variant="outlined"
                    fullWidth
                    value={editUser.contacts.tel2}
                    type={"number"}
                    name={"contacts.tel2"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть ваш номер мобільного телефону #2"}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Паспорт
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={"Вкажіть серію та номер"}
                    label={"Серія та номер"}
                    variant="outlined"
                    fullWidth
                    value={editUser.passport?.seriesNumber}
                    type={"string"}
                    name={"passport.seriesNumber"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть серію та номер вашого паспорта"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={"Вкажіть ким видано"}
                    label={"Ким видано"}
                    variant="outlined"
                    fullWidth
                    value={editUser.passport?.issuedBy}
                    type={"string"}
                    name={"passport.issuedBy"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть ким видано ваш паспорт"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={"Вкажіть ID паспорт (або картку)"}
                    label={"ID паспорт (або картка)"}
                    variant="outlined"
                    fullWidth
                    value={editUser.passport?.idPassport}
                    type={"string"}
                    name={"passport.idPassport"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть ID паспорт (або картку)"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={"Вкажіть код УНЗР"}
                    label={"Код УНЗР"}
                    variant="outlined"
                    fullWidth
                    value={editUser.passport?.unzr}
                    type={"string"}
                    name={"passport.unzr"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть код УНЗР"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={"Вкажіть ким видано (ID паспорт)"}
                    label={"Ким видано (ID паспорт)"}
                    variant="outlined"
                    fullWidth
                    value={editUser.passport?.issuedById}
                    type={"string"}
                    name={"passport.issuedById"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть ким видано (ID паспорт)"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <MobileDatePicker
                    label={"Дата видачі"}
                    inputFormat="MM/dd/yyyy"
                    value={new Date(editUser.passport?.dateOfIssueId)}
                    onChange={(newDate) => {
                      const newState = {
                        ...editUser,
                      };
                      newState.passport.dateOfIssueId = newDate;
                      setEditUser(newState);
                    }}
                    renderInput={(params) => (
                      <TextField fullWidth {...params} />
                    )}
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Стаж
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={"Вкажіть ваш науковий стаж (років)"}
                    label={"Науковий стаж (років)"}
                    variant="outlined"
                    fullWidth
                    value={editUser.experience?.scientific}
                    type={"number"}
                    name={"experience.scientific"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть ваш науковий стаж (років)"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={"Вкажіть ваш педагогічний стаж (років)"}
                    label={"Педагогічний стаж (років)"}
                    variant="outlined"
                    fullWidth
                    value={editUser.experience?.pedagogical}
                    type={"number"}
                    name={"experience.pedagogical"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть ваш педагогічний стаж (років)"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={
                      "Вкажіть ваш науково-педагогічний стаж (років)"
                    }
                    label={"Науково-педагогічний стаж (років)"}
                    variant="outlined"
                    fullWidth
                    value={editUser.experience?.scientificAndPedagogical}
                    type={"number"}
                    name={"experience.scientificAndPedagogical"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть ваш науково-педагогічний стаж (років)"}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Поточна посада
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={"Вкажіть вашу основну посаду"}
                    label={"Основна посада"}
                    variant="outlined"
                    fullWidth
                    value={editUser.position?.jobTitle}
                    type={"string"}
                    name={"position.jobTitle"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть вашу основну посаду"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={"Вкажіть вашу посаду (сумісництво)"}
                    label={"Посада (сумісництво)"}
                    variant="outlined"
                    fullWidth
                    value={editUser.position?.moonlighting}
                    type={"string"}
                    name={"position.moonlighting"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть вашу посаду (сумісництво)"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={"Вкажіть назву кафедри"}
                    label={"Назва кафедри"}
                    variant="outlined"
                    fullWidth
                    value={editUser.position?.nameOfDepartment}
                    type={"string"}
                    name={"position.nameOfDepartment"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть назву кафедри"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <TextField
                    placeholder={"Вкажіть назву інституту (факультету)"}
                    label={"Назва інституту (факультету)"}
                    variant="outlined"
                    fullWidth
                    value={editUser.position?.nameOfInstitute}
                    type={"string"}
                    name={"position.nameOfInstitute"}
                    onChange={handleInputChange}
                    helperText={"Вкажіть назву інституту (факультету)"}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <MobileDatePicker
                    label={"Дата початку стажу"}
                    inputFormat="MM/dd/yyyy"
                    value={new Date(editUser.position?.internshipStartDate)}
                    onChange={(newDate) => {
                      const newState = {
                        ...editUser,
                      };
                      newState.position.internshipStartDate = newDate;
                      setEditUser(newState);
                    }}
                    renderInput={(params) => (
                      <TextField fullWidth {...params} />
                    )}
                    sx={{ width: "100%" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <MobileDatePicker
                    label={"Початок роботи в закладі"}
                    inputFormat="MM/dd/yyyy"
                    value={
                      new Date(editUser.position?.startWorkingInInstitution)
                    }
                    onChange={(newDate) => {
                      const newState = {
                        ...editUser,
                      };
                      newState.position.startWorkingInInstitution = newDate;
                      setEditUser(newState);
                    }}
                    renderInput={(params) => (
                      <TextField fullWidth {...params} />
                    )}
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Button onClick={updateUserInfo} variant={"contained"}>
            Оновити
          </Button>
        </Grid>
      </Grid>
    </ErrorBoundary>
  );
}

export default MainUserInfo;
